import { MenuConfig, MenuItem } from '@/config/types';
import { MENU_SIDEBAR_MAIN, MODULE_CONFIG } from '@/config/layout.config';

export type PortalType = 'tenant' | 'platform';
export type TenantModule = 'dashboard' | 'crm' | 'inventory' | 'service' | 'finance' | 'reporting';

/**
 * Detect the active tenant module from a URL path. Mirrors the sidebar's
 * resolution so breadcrumbs and the navigation menu stay in lock-step.
 */
export function detectModuleFromPath(pathname: string): TenantModule {
  if (pathname.startsWith('/crm')) return 'crm';
  if (pathname.startsWith('/inventory')) return 'inventory';
  if (pathname.startsWith('/service')) return 'service';
  if (pathname.startsWith('/finance')) return 'finance';
  if (pathname.startsWith('/reporting')) return 'reporting';
  return 'dashboard';
}

/**
 * The flat menu that applies to the current route. Platform routes use the
 * platform admin menu; tenant routes use the active module's dashboard plus
 * all of its sections — the same source the sidebar renders from.
 */
export function resolveMenuForPath(
  pathname: string,
  portalType: PortalType,
): MenuConfig {
  if (portalType === 'platform') {
    return MENU_SIDEBAR_MAIN;
  }
  const moduleConfig = MODULE_CONFIG[detectModuleFromPath(pathname)];
  if (!moduleConfig) return [];
  return [
    ...moduleConfig.dashboard,
    ...moduleConfig.sections.flatMap((section) => section.menu),
  ];
}

/** The landing page each portal's "Home" breadcrumb points to. */
export const HOME_PATH = '/dashboard';

/**
 * The dashboard path for the route's active module — used to link structural
 * section crumbs (e.g. "Inventory", which has no path of its own) to a real
 * destination instead of "#". Falls back to the global home.
 */
export function resolveModuleHomePath(
  pathname: string,
  portalType: PortalType,
): string {
  if (portalType === 'platform') return HOME_PATH;
  const moduleConfig = MODULE_CONFIG[detectModuleFromPath(pathname)];
  return moduleConfig?.dashboard?.[0]?.children?.[0]?.path ?? HOME_PATH;
}

/**
 * True when a menu path "owns" the current pathname — an exact match or a
 * parent segment (with a `/` boundary so `/inventory/boat` never matches
 * `/inventory/boats`).
 */
function pathOwns(menuPath: string | undefined, pathname: string): boolean {
  if (!menuPath || menuPath === '#') return false;
  if (menuPath === '/') return pathname === '/';
  return pathname === menuPath || pathname.startsWith(`${menuPath}/`);
}

/**
 * Build the breadcrumb trail for a route. Walks the resolved menu and returns
 * the ancestor chain of the *most specific* (longest) matching path, so a
 * module dashboard prefix (e.g. `/inventory`) never shadows a deeper section
 * leaf (e.g. `/inventory/boats`). Title-less structural wrapper nodes are
 * dropped — only navigable/labelled items remain.
 */
export function buildBreadcrumbTrail(
  pathname: string,
  portalType: PortalType,
): MenuItem[] {
  const menu = resolveMenuForPath(pathname, portalType);
  let best: { length: number; chain: MenuItem[] } = { length: -1, chain: [] };

  const walk = (nodes: MenuConfig, ancestors: MenuItem[]) => {
    for (const node of nodes) {
      const chain = [...ancestors, node];
      if (node.path && pathOwns(node.path, pathname) && node.path.length > best.length) {
        best = { length: node.path.length, chain };
      }
      if (node.children && node.children.length > 0) {
        walk(node.children, chain);
      }
    }
  };

  walk(menu, []);
  return best.chain.filter((item) => Boolean(item.title));
}
